import React, { useState } from 'react';

import QueryCard from 'components/Meteorology/Shared/QueryCard';
import QueryCardHeader from 'components/Meteorology/Shared/QueryCard/Header';
import QueryCardChips from 'components/Meteorology/Shared/QueryCard/Chips';
import QueryCardChip from 'components/Meteorology/Shared/QueryCard/Chip';
import QueryCardTrigger from 'components/Meteorology/Shared/QueryCard/Trigger';
import Icon from 'components/DesignSystem/Icon';
import Segmented from 'components/DesignSystem/Segmented';
import SelectorChip from 'components/DesignSystem/SelectorChip';
import useMapsControl from 'hooks/Meteorology/Agro/CultureIndex/Area/useMapsControl';
import { cssVar } from 'constants/theme';

import MapDisplay from './MapDisplay';
import ControlPanelSheet from './ControlPanelSheet';
import InfoSheet from './InfoSheet';

import './index.scss';

const MAPS_MAX = 2;

type LayoutMode = 'empilhado' | 'abas';

export default function AreaMobile() {
  const {
    maps, addMapDisabled, removeMapDisabled, addMap, removeMap,
  } = useMapsControl();

  const [panelOpen, setPanelOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('empilhado');
  const [viewMapId, setViewMapId] = useState<number>(maps[0]?.id ?? 0);

  const multi = maps.length > 1;
  const atMax = maps.length >= MAPS_MAX;

  const activeViewMap = maps.find((m) => m.id === viewMapId) ?? maps[0];
  const visibleMaps = multi && layoutMode === 'abas' ? [activeViewMap] : maps;

  const summaryTitle = multi ? 'Comparando 2 mapas' : (maps[0]?.params.culture?.label ?? 'Índice por área');
  const chips = maps
    .map((m, index) => (m.params.culture?.label
      ? `${multi ? `Mapa ${index + 1}: ` : ''}${m.params.culture.label}`
      : null))
    .filter((c): c is string => c !== null);

  return (
    <div className="agro-ci-area-mobile">
      <QueryCard>
        <QueryCardHeader
          label="Consulta"
          title={summaryTitle}
          action={(
            <button
              type="button"
              aria-label="Instruções"
              className="agro-ci-area-mobile-info-trigger"
              onClick={() => setInfoOpen(true)}
            >
              <Icon iconType="info" color={cssVar('text-strong')} size="small" />
            </button>
          )}
        >
          {chips.length > 0 && (
            <QueryCardChips>
              {chips.map((chip) => <QueryCardChip key={chip}>{chip}</QueryCardChip>)}
            </QueryCardChips>
          )}
        </QueryCardHeader>
        <QueryCardTrigger
          onClick={() => setPanelOpen(true)}
          hint={multi ? 'Filtros por mapa · Período' : 'Cultura · Data · Período'}
        />
      </QueryCard>

      <div className="agro-ci-area-mobile-toolbar">
        <div className="agro-ci-area-mobile-counter">
          <span className="agro-ci-area-mobile-counter-text">
            {`${maps.length} de ${MAPS_MAX} mapas`}
            {atMax && <span className="agro-ci-area-mobile-counter-limit"> · limite atingido</span>}
          </span>
          <span className="agro-ci-area-mobile-pips">
            {Array.from({ length: MAPS_MAX }).map((_, index) => (
              <span
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                className={`agro-ci-area-mobile-pip${index < maps.length ? ' filled' : ''}`}
              />
            ))}
          </span>
        </div>
        <button
          type="button"
          className="agro-ci-area-mobile-add-btn"
          onClick={addMap}
          disabled={addMapDisabled}
        >
          <Icon iconType="add" color={cssVar(addMapDisabled ? 'text-disabled' : 'text-on-accent')} size="small" />
          Adicionar mapa
        </button>
      </div>

      {multi && (
        <Segmented
          className="agro-ci-area-mobile-layout-toggle"
          size="sm"
          fill={false}
          ariaLabel="Modo de visualização"
          options={[
            {
              value: 'empilhado',
              ariaLabel: 'Empilhado',
              iconNode: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="4" width="16" height="7" rx="1.5" />
                  <rect x="4" y="13" width="16" height="7" rx="1.5" />
                </svg>
              ),
            },
            {
              value: 'abas',
              ariaLabel: 'Um por vez',
              iconNode: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="7" width="16" height="10" rx="1.5" />
                </svg>
              ),
            },
          ]}
          value={layoutMode}
          onChange={setLayoutMode}
        />
      )}

      {multi && layoutMode === 'abas' && (
        <div className="agro-ci-area-mobile-view-tabs" role="tablist">
          {maps.map((m, index) => (
            <SelectorChip
              key={m.id}
              role="tab"
              shape="round"
              selected={m.id === activeViewMap.id}
              className="agro-ci-area-mobile-view-tab"
              onClick={() => setViewMapId(m.id)}
            >
              {`Mapa ${index + 1}`}
            </SelectorChip>
          ))}
        </div>
      )}

      <div className="agro-ci-area-mobile-maps">
        {visibleMaps.map((areaMap) => (
          <MapDisplay
            key={areaMap.id}
            areaMap={areaMap}
            position={maps.findIndex((m) => m.id === areaMap.id) + 1}
            totalMaps={maps.length}
            removeMap={removeMap}
            removeMapDisabled={removeMapDisabled}
          />
        ))}
      </div>

      <ControlPanelSheet open={panelOpen} dismiss={() => setPanelOpen(false)} maps={maps} />
      <InfoSheet open={infoOpen} dismiss={() => setInfoOpen(false)} />
    </div>
  );
}
